export const serviceDetails = [
  {
    slug: 'roth-conversions',
    heroHeadline: 'Pay Taxes Once. Never Again.',
    intro:
      'A Roth conversion moves money from a pre-tax retirement account into a Roth IRA — you pay income tax on it now, and it grows completely tax-free from that point forward. For the right client, this is one of the most powerful moves in retirement planning: a one-time tax bill that buys you decades of tax-free income.',
    benefits: [
      {
        title: 'Your Money Grows Tax-Free — Forever',
        body: 'Once funds are inside a Roth IRA, every dollar of growth, every dividend, and every withdrawal in retirement is 100% tax-free. Unlike a traditional IRA or 401(k), you are never taxed again on that money — no matter how large the account grows.',
      },
      {
        title: 'No Required Minimum Distributions',
        body: 'Traditional IRAs force you to take taxable withdrawals starting at age 73, whether you need the money or not. Roth IRAs have no required minimum distributions, so your money can keep compounding on your timeline — not the IRS\'s.',
      },
      {
        title: 'Lock In Today\'s Tax Rates',
        body: 'Many retirees are in a lower tax bracket in the years between retirement and age 73 — before Social Security, pensions, and RMDs all hit at once. Converting during that window lets you pay taxes at today\'s lower rates instead of tomorrow\'s potentially higher ones.',
      },
      {
        title: 'A Tax-Free Legacy for Your Heirs',
        body: 'A Roth IRA passes to your beneficiaries income-tax-free, giving them a meaningful financial head start without a tax bill attached. It is one of the most efficient wealth-transfer tools available.',
      },
    ],
    steps: [
      {
        step: '01',
        title: 'Identify the Right Window',
        desc: 'Dr. Patel reviews your income, tax bracket, and projected RMDs to find the years — often between retirement and age 73 — where a conversion costs you the least.',
      },
      {
        step: '02',
        title: 'Convert Strategically, Not All at Once',
        desc: 'Rather than converting everything in one year and spiking your tax bill, we convert in carefully sized annual amounts to stay within the most favorable tax brackets.',
      },
      {
        step: '03',
        title: 'Let the Tax-Free Compounding Begin',
        desc: 'Once converted, your Roth balance grows without any future tax drag, and you withdraw it in retirement entirely tax-free — on your schedule.',
      },
    ],
    callout: 'A client who converts $300,000 over five years at a 22% rate saves over $80,000 compared to taking those same dollars as taxable RMDs at a 32% rate later.',
    closing: 'The window to convert at today\'s tax rates will not stay open forever. Schedule a complimentary review with Dr. Patel to find out exactly how much a Roth conversion could save your family.',
  },
  {
    slug: 'long-term-care',
    heroHeadline: '$100K Invested Becomes $300K in Care Coverage.',
    intro:
      'The average cost of a private nursing home room is over $100,000 per year — and 70% of Americans turning 65 will need some form of long-term care. A hybrid long-term care policy turns a single lump-sum deposit into three times or more in guaranteed care benefits, while keeping your premium fully protected if you never need the care at all.',
    benefits: [
      {
        title: 'Triple Your Money for Care',
        body: 'With a hybrid long-term care policy, a $100,000 deposit can generate $300,000 or more in a tax-free pool of benefits designated for home care, assisted living, or nursing home expenses.',
      },
      {
        title: "Don't Use It, Don't Lose It",
        body: "Unlike traditional LTC insurance where every premium dollar is gone if you stay healthy, a hybrid policy is built on a life insurance chassis. If you pass away never having needed care, your beneficiaries receive the full death benefit income-tax-free.",
      },
      {
        title: 'Premiums Locked and Guaranteed',
        body: 'Traditional long-term care policies have a history of dramatic premium increases — some jumping 50% or more. Hybrid policies guarantee your premium never increases, giving you certainty and peace of mind.',
      },
      {
        title: 'Benefits Paid Tax-Free',
        body: 'Long-term care benefit payments from a qualified policy are generally received income-tax-free, so every dollar of your care pool goes directly toward your care costs — with none lost to the IRS.',
      },
    ],
    steps: [
      {
        step: '01',
        title: 'Fund the Policy',
        desc: 'Most clients use a single lump-sum premium — often $75,000 to $150,000 from savings or a 1035 exchange — to immediately activate full coverage.',
      },
      {
        step: '02',
        title: 'Benefits Activate When You Need Care',
        desc: 'If you are unable to perform two of six Activities of Daily Living or are diagnosed with cognitive impairment, the policy begins paying your benefit pool tax-free for qualified care expenses.',
      },
      {
        step: '03',
        title: 'What Remains Passes to Your Heirs',
        desc: 'Any unused portion of your benefit pool, or the full death benefit if care was never needed, transfers to your named beneficiaries income-tax-free.',
      },
    ],
    callout: '70% of Americans turning 65 will need some form of long-term care in their lifetime — yet fewer than 10% have a formal plan to pay for it. The average long-term care event lasts over 3 years.',
    closing: 'The best time to secure long-term care coverage is before you need it — and before a health change makes you uninsurable. Let Dr. Patel show you exactly what your deposit could generate in care dollars.',
  },
  {
    slug: 'life-insurance',
    heroHeadline: 'Tax-Free Income for Life. Your Family Keeps Every Dollar.',
    intro:
      'Permanent life insurance — specifically an Indexed Universal Life policy structured correctly — does far more than pay a death benefit. It builds cash value tied to index performance, protects that cash value from market losses, and lets you access it as tax-free income in retirement. It is the only financial vehicle that does all three simultaneously.',
    benefits: [
      {
        title: 'Tax-Free Retirement Income With No Contribution Limits',
        body: 'Unlike a 401(k) or IRA, there is no government cap on how much you can fund a properly structured IUL policy. The cash value grows tax-deferred and you access it in retirement through policy loans that are not reported as taxable income.',
      },
      {
        title: 'Market Upside, Zero Downside',
        body: "An IUL's cash value is credited based on the performance of an index like the S&P 500, but is never directly invested in the market. When the market drops, your account floor holds at zero — not a loss.",
      },
      {
        title: 'A Tax-Free Death Benefit',
        body: 'The death benefit from a life insurance policy passes to your beneficiaries completely income-tax-free under federal law. Whether that benefit is $500,000 or $5 million, your family receives every dollar.',
      },
      {
        title: 'Living Benefits for Critical Illness',
        body: 'Many modern policies include accelerated benefit riders that allow you to access a portion of your death benefit while still living if you are diagnosed with a terminal, chronic, or critical illness.',
      },
    ],
    steps: [
      {
        step: '01',
        title: 'Structure for Maximum Cash Value',
        desc: 'Dr. Patel engineers the policy to minimize the death benefit and maximize allowable premiums, directing as much money as legally permitted into the tax-advantaged cash value.',
      },
      {
        step: '02',
        title: 'Fund and Let Cash Value Accumulate',
        desc: 'Over a 10–20 year window, your cash value grows linked to index performance, protected by a floor against losses, and compounds without annual tax drag.',
      },
      {
        step: '03',
        title: 'Take Tax-Free Income in Retirement',
        desc: 'You borrow against your policy\'s cash value — these loans are not taxable income, do not appear on your tax return, and can continue for life.',
      },
    ],
    callout: 'A client who puts $3,000 per month into a properly structured IUL for 20 years can realistically draw $8,000–$12,000 per month tax-free in retirement — income that never shows up on a tax return.',
    closing: 'Tax-free income is rare, powerful, and worth planning for now — before your health or age changes your insurability. Schedule a no-obligation illustration with Dr. Patel.',
  },
  {
    slug: 'annuities',
    heroHeadline: 'A Paycheck You Cannot Outlive. Guaranteed.',
    intro:
      'An annuity converts your savings into a guaranteed income stream that continues for as long as you are alive — no matter what the stock market does. A fixed indexed annuity provides market-linked growth potential, full principal protection, and a lifetime income guarantee in a single product.',
    benefits: [
      {
        title: 'Your Principal Cannot Go Backward',
        body: 'A fixed indexed annuity guarantees your principal against market loss. When the index drops, your account stays flat — it does not fall. When the index rises, you receive a portion of those gains, permanently locked in.',
      },
      {
        title: 'Guaranteed Income for Life',
        body: 'With a lifetime income rider, your annuity guarantees a specific monthly payment for as long as you live — even if you outlive the account\'s cash value entirely. No stock portfolio can match this guarantee.',
      },
      {
        title: 'Tax-Deferred Growth',
        body: 'Annuity earnings grow tax-deferred, meaning you do not owe taxes on gains until you take income — allowing your money to compound faster than it would in a taxable account.',
      },
      {
        title: 'Spouse and Legacy Protection',
        body: 'Most annuities allow a joint-life option so your spouse continues to receive income after you pass, and many include a return-of-premium death benefit ensuring your heirs receive at least what you deposited.',
      },
    ],
    steps: [
      {
        step: '01',
        title: 'Deposit and Set Your Accumulation Period',
        desc: 'You fund the annuity with a lump sum or payments, and during accumulation your account grows tax-deferred, linked to an index with a guaranteed floor of zero.',
      },
      {
        step: '02',
        title: 'Activate Your Guaranteed Income',
        desc: 'When you are ready to retire, you turn on the income rider, converting your accumulated benefit base into guaranteed monthly payments that cannot stop while you are alive.',
      },
      {
        step: '03',
        title: 'Collect for Life, Pass On the Rest',
        desc: 'You receive guaranteed income for as long as you live, and any remaining account value or death benefit passes to your named beneficiaries outside of probate.',
      },
    ],
    callout: 'Fixed indexed annuity sales hit record highs as retirees sought principal protection and guaranteed income. For clients who want certainty, no other product delivers the same combination of safety and growth.',
    closing: 'If you want retirement income that never stops and a principal that can never go backward, an annuity may be the most important piece of your retirement plan. Let Dr. Patel run a guaranteed income illustration using your specific numbers.',
  },
];
